/**
 * Shared Queue Layout Components
 *
 * Consistent styling across All Projects, PA View, and Assembly View
 */

import { useState } from 'react';

// ============================================================================
// LAYOUT CONSTANTS
// ============================================================================

export const LAYOUT = {
  // Page container
  pageClass: 'flex-1 bg-slate-200 text-gray-900 overflow-auto',
  contentClass: 'max-w-7xl mx-auto px-4 py-4',

  // Two-column widths
  mainColWidth: 'w-[65%]',
  sideColWidth: 'w-[35%]',

  // Kanban
  kanbanHeight: 'max-h-56',
  kanbanCardHeight: 'max-h-[280px]',

  // Spacing
  sectionGap: 'space-y-4',
  columnGap: 'gap-4',
};

// ============================================================================
// STAGE/BUCKET COLORS
// ============================================================================

export const STAGE_COLORS = {
  // Canonical stages
  PENDING: { bg: 'bg-gray-100', header: 'bg-gray-200', text: 'text-gray-700', dot: 'bg-gray-400', border: 'border-gray-300' },
  PROD_ART: { bg: 'bg-purple-50', header: 'bg-purple-200', text: 'text-purple-700', dot: 'bg-purple-500', border: 'border-purple-300' },
  PRINTING: { bg: 'bg-blue-50', header: 'bg-blue-200', text: 'text-blue-700', dot: 'bg-blue-500', border: 'border-blue-300' },
  ASSEMBLY: { bg: 'bg-green-50', header: 'bg-green-200', text: 'text-green-700', dot: 'bg-green-500', border: 'border-green-300' },
  SHIPPING: { bg: 'bg-orange-50', header: 'bg-orange-200', text: 'text-orange-700', dot: 'bg-orange-500', border: 'border-orange-300' },
  COMPLETE: { bg: 'bg-teal-50', header: 'bg-teal-200', text: 'text-teal-700', dot: 'bg-teal-500', border: 'border-teal-300' },

  // PA-specific
  gray: { bg: 'bg-gray-50', header: 'bg-gray-100', text: 'text-gray-700', dot: 'bg-gray-400', border: 'border-gray-300' },
  blue: { bg: 'bg-blue-50/50', header: 'bg-blue-100', text: 'text-blue-700', dot: 'bg-blue-500', border: 'border-blue-300' },
  yellow: { bg: 'bg-yellow-50/50', header: 'bg-yellow-100', text: 'text-yellow-700', dot: 'bg-yellow-500', border: 'border-yellow-300' },
  amber: { bg: 'bg-amber-50/50', header: 'bg-amber-100', text: 'text-amber-700', dot: 'bg-amber-500', border: 'border-amber-300' },
  orange: { bg: 'bg-orange-50/50', header: 'bg-orange-100', text: 'text-orange-700', dot: 'bg-orange-500', border: 'border-orange-300' },
  green: { bg: 'bg-green-50/50', header: 'bg-green-100', text: 'text-green-700', dot: 'bg-green-500', border: 'border-green-300' },
  purple: { bg: 'bg-purple-50/50', header: 'bg-purple-100', text: 'text-purple-700', dot: 'bg-purple-500', border: 'border-purple-300' },
  red: { bg: 'bg-red-50/50', header: 'bg-red-100', text: 'text-red-700', dot: 'bg-red-500', border: 'border-red-300' },
};

// ============================================================================
// PAGE HEADER
// ============================================================================

export function QueueHeader({ title, subtitle, children }) {
  return (
    <div className="bg-white border-b border-gray-200 px-4 py-3 -mx-4 -mt-4 mb-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-bold text-gray-900">{title}</h1>
          {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
        </div>
        <div className="flex items-center gap-3">
          {children}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// KANBAN OVERVIEW - Matches PA View and Assembly View format exactly
// ============================================================================

export function KanbanOverview({ stages, data, onCardClick, workflowLabel = 'Workflow' }) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
      {/* Workflow label */}
      <div className="flex items-center gap-2 mb-3 text-xs text-gray-400">
        <span>{workflowLabel}</span>
        <div className="flex-1 h-px bg-gradient-to-r from-gray-200 to-transparent" />
      </div>

      <div className="flex items-stretch">
        {stages.map((stage, index) => {
          const items = data[stage.id] || [];
          const colors = STAGE_COLORS[stage.id] || STAGE_COLORS[stage.color] || STAGE_COLORS.gray;
          const isLast = index === stages.length - 1;

          return (
            <div key={stage.id} className="flex items-stretch flex-1 min-w-0">
              <KanbanColumn
                label={stage.label}
                count={items.length}
                items={items}
                colors={colors}
                onCardClick={onCardClick}
                stepNumber={index + 1}
              />
              {/* Arrow between columns */}
              {!isLast && (
                <div className="flex items-center px-1 text-gray-300">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function KanbanColumn({ label, count, items, colors, onCardClick, stepNumber }) {
  // Calculate bucket totals
  const totalSKUs = items.reduce((sum, item) => sum + (item.total_job_count || item.jobCount || 0), 0);
  const totalQty = items.reduce((sum, item) => sum + (item.total_qty || item.totalQty || 0), 0);

  return (
    <div className="flex-1 min-w-0 flex flex-col">
      {/* Column header with step number */}
      <div className={`px-3 py-2 rounded-t-lg border ${colors.header} ${colors.bg}`}>
        <div className="flex items-center gap-2">
          <span className={`w-5 h-5 rounded-full ${colors.dot} text-white text-[10px] font-bold flex items-center justify-center`}>
            {stepNumber}
          </span>
          <span className={`text-xs font-bold truncate ${colors.text}`}>{label}</span>
        </div>
        <div className={`text-xs font-medium mt-1 opacity-70 ${colors.text}`}>
          {count} project{count !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Column body - show project rows with scrolling */}
      <div className={`flex-1 border border-t-0 border-gray-200 px-1.5 py-1.5 ${colors.bg} ${LAYOUT.kanbanCardHeight} overflow-y-auto`}>
        {/* Column headers for SKUs/Qty */}
        <div className="flex items-center gap-1.5 px-1.5 py-0.5 text-[9px] text-gray-400 font-medium border-b border-gray-200 mb-1">
          <span className="w-12 flex-shrink-0">Quote</span>
          <span className="flex-1 truncate">Project</span>
          <span className="w-6 text-right">SKU</span>
          <span className="w-10 text-right">Qty</span>
        </div>
        {items.length === 0 ? (
          <div className="text-xs text-gray-400 text-center py-3 italic">No projects</div>
        ) : (
          <div className="space-y-0.5">
            {items.map((item) => (
              <KanbanRow key={item.id || item.quote_number || item.quoteNumber} item={item} onClick={onCardClick} />
            ))}
          </div>
        )}
      </div>

      {/* Column footer - totals */}
      <div className={`border border-t-0 border-gray-200 rounded-b-lg px-1.5 py-1.5 ${colors.bg}`}>
        <div className="flex items-center gap-1.5 px-1.5 py-0.5 text-[10px] font-semibold border-t border-gray-300 pt-1">
          <span className="flex-1 text-gray-600">Total:</span>
          <span className="w-6 text-right text-blue-700">{totalSKUs}</span>
          <span className="w-10 text-right text-indigo-700">{totalQty.toLocaleString()}</span>
        </div>
      </div>
    </div>
  );
}

function KanbanRow({ item, onClick }) {
  const quoteNumber = item.quote_number || item.quoteNumber || item.woNumber;
  const projectName = item.project_name || item.projectName;
  const isRush = item.has_rush || item.isRush;
  const hasActive = item.has_active_tickets || item.activeWorkers?.length > 0;
  const jobCount = item.total_job_count || item.jobCount || 0;
  const totalQty = item.total_qty || item.totalQty || 0;
  const hasIncompleteMilestones = item.has_incomplete_milestones;

  return (
    <div
      onClick={() => onClick?.(item)}
      className="flex items-center gap-1.5 px-1.5 py-0.5 rounded cursor-pointer hover:bg-white transition-colors text-[10px]"
    >
      {isRush && (
        <span className="px-1 py-0.5 bg-red-100 text-red-600 text-[8px] rounded font-bold">RUSH</span>
      )}
      {hasIncompleteMilestones && (
        <span className="text-amber-500 flex-shrink-0" title="Incomplete milestones - needs investigation">
          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        </span>
      )}
      {hasActive && (
        <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse flex-shrink-0" />
      )}
      <span className="font-mono text-purple-600 font-medium w-12 flex-shrink-0">{quoteNumber}</span>
      <span className="text-gray-700 truncate flex-1 font-medium">{projectName || 'Untitled'}</span>
      <span className="text-blue-600 font-semibold w-6 text-right" title="Jobs">{jobCount}</span>
      <span className="text-indigo-600 font-semibold w-10 text-right" title="Qty">{totalQty?.toLocaleString?.() || totalQty}</span>
    </div>
  );
}

// ============================================================================
// COLLAPSIBLE SECTION
// ============================================================================

export function Section({ title, count, color = 'gray', children, defaultCollapsed = true }) {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);
  const colors = STAGE_COLORS[color] || STAGE_COLORS.gray;

  return (
    <section className="mb-4">
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className={`
          w-full flex items-center gap-2 text-xs font-semibold uppercase tracking-wider
          px-3 py-2 rounded-lg cursor-pointer transition-all
          border-l-4 ${colors.border} ${colors.bg}
          hover:shadow-sm
        `}
      >
        <svg
          className={`w-3.5 h-3.5 transition-transform ${isCollapsed ? '' : 'rotate-90'}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        <span className={`w-2 h-2 rounded-full ${colors.dot}`} />
        <span className={colors.text}>{title}</span>
        <span className={`ml-auto px-2 py-0.5 ${colors.header} rounded-full text-xs font-medium ${colors.text}`}>
          {count}
        </span>
      </button>

      {!isCollapsed && (
        <div className="mt-2 bg-white rounded-lg border border-gray-200 overflow-hidden">
          {children}
        </div>
      )}
    </section>
  );
}

// ============================================================================
// EMPTY STATE
// ============================================================================

export function EmptyRow({ children }) {
  return (
    <div className="px-4 py-6 text-center text-sm text-gray-400 italic">
      {children}
    </div>
  );
}

// ============================================================================
// PROJECT ROW - Compact row for list view
// ============================================================================

export function ProjectRow({ project, onClick, children }) {
  const quoteNumber = project.quote_number || project.quoteNumber || project.woNumber;
  const projectName = project.project_name || project.projectName;
  const pm = project.pm;
  const jobCount = project.total_job_count || project.jobCount || 0;
  const qty = project.total_qty || project.totalComps || project.totalQty || 0;
  const isRush = project.has_rush || project.isRush;
  const isOverdue = project.days_until_due !== null && project.days_until_due < 0;
  const hasActive = project.has_active_tickets || project.activeWorkers?.length > 0;
  const shipDate = project.ship_date_formatted || project.shipDate;

  return (
    <div
      onClick={() => onClick?.(project)}
      className={`
        flex items-center gap-3 px-3 py-2 border-b border-gray-100 last:border-b-0
        hover:bg-gray-50 cursor-pointer transition-colors
        ${isRush ? 'bg-red-50 border-l-4 border-l-red-500' : ''}
        ${isOverdue && !isRush ? 'bg-orange-50' : ''}
      `}
    >
      {/* Quote ID */}
      <div className="w-20 flex-shrink-0">
        <span className="font-mono text-sm font-semibold text-gray-900">{quoteNumber}</span>
      </div>

      {/* Project Name + PM */}
      <div className="flex-1 min-w-0">
        <p className="text-sm text-gray-800 truncate">{projectName || 'Untitled'}</p>
        <p className="text-xs text-gray-500 truncate">{pm || 'No PM'}</p>
      </div>

      {/* SKUs / Qty */}
      <div className="w-14 text-center flex-shrink-0">
        <span className="text-sm font-medium text-gray-900">{jobCount}</span>
        <span className="text-xs text-gray-400 block">SKUs</span>
      </div>
      <div className="w-14 text-center flex-shrink-0">
        <span className="text-sm font-medium text-gray-900">{qty}</span>
        <span className="text-xs text-gray-400 block">Qty</span>
      </div>

      {/* Ship Date */}
      <div className="w-16 text-right flex-shrink-0">
        {shipDate ? (
          <span className={`text-xs font-medium ${isOverdue ? 'text-red-600' : 'text-gray-500'}`}>
            {shipDate}
          </span>
        ) : (
          <span className="text-xs text-gray-300">-</span>
        )}
      </div>

      {/* Status indicators */}
      <div className="w-16 flex items-center justify-end gap-1 flex-shrink-0">
        {isRush && (
          <span className="px-1.5 py-0.5 bg-red-500 text-white text-[10px] font-bold rounded">RUSH</span>
        )}
        {hasActive && (
          <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" title="Active work" />
        )}
      </div>

      {/* Custom actions */}
      {children}
    </div>
  );
}

// ============================================================================
// RIGHT PANEL
// ============================================================================

export function RightPanel({ children }) {
  return (
    <div className={`${LAYOUT.sideColWidth} flex-shrink-0`}>
      <div className="sticky top-24 space-y-4">
        {children}
      </div>
    </div>
  );
}

export function RightPanelCard({ title, children }) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      {title && <h3 className="text-sm font-semibold text-gray-700 mb-3">{title}</h3>}
      {children}
    </div>
  );
}

export function RightPanelPlaceholder({ title = 'Coming Soon', description }) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 border-dashed p-6 text-center">
      <div className="text-gray-400 mb-2">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
        </svg>
      </div>
      <p className="text-sm text-gray-500 font-medium">{title}</p>
      {description && <p className="text-xs text-gray-400 mt-1">{description}</p>}
    </div>
  );
}

// ============================================================================
// FILTERS
// ============================================================================

export function SearchInput({ value, onChange, placeholder = 'Search...' }) {
  return (
    <div className="relative">
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg w-36 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
      />
      <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
      </svg>
      {value && (
        <button
          onClick={() => onChange('')}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  );
}

export function PMFilter({ pms, selected, onChange }) {
  return (
    <select
      value={selected}
      onChange={(e) => onChange(e.target.value)}
      className="text-sm border border-gray-300 rounded-lg px-2 py-1.5 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
    >
      <option value="">All PMs</option>
      {pms.map((pm) => (
        <option key={pm} value={pm}>{pm}</option>
      ))}
    </select>
  );
}

export function RefreshButton({ onClick, loading }) {
  return (
    <button
      onClick={onClick}
      disabled={loading}
      className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded disabled:opacity-50"
      title="Refresh"
    >
      <svg className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
      </svg>
    </button>
  );
}

export function LastUpdated({ timestamp }) {
  if (!timestamp) return null;
  return (
    <span className="text-xs text-gray-400">
      {timestamp.toLocaleTimeString()}
    </span>
  );
}
